import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AddDoctorForm = ({ onAddDoctor, onCancel }) => {
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const [specialties, setSpecialties] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    qualification: '',
    experience: '',
    fees: '',
    image: '',
    availability: []
  });

  const [availabilityInput, setAvailabilityInput] = useState({
    day: 'Monday',
    startTime: '',
    endTime: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const formDataToUpload = new FormData();
      formDataToUpload.append('image', file);

      try {
        const response = await axios.post('/api/doctors/upload', formDataToUpload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        console.log(response)
        setFormData({ ...formData, image: response.data.imagePath }); // Save the uploaded image path
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }
  };

  const handleAvailabilityChange = (e) => {
    const { name, value } = e.target;
    setAvailabilityInput({ ...availabilityInput, [name]: value });
  };

  const addAvailability = () => {
    if (!availabilityInput.startTime || !availabilityInput.endTime) {
      alert('Please provide both start and end times');
      return;
    }

    // Check if day already exists in availability
    const dayExists = formData.availability.some(
      slot => slot.day === availabilityInput.day
    );

    if (dayExists) {
      alert(`Availability for ${availabilityInput.day} already exists. Please edit or remove it first.`);
      return;
    }

    setFormData({
      ...formData,
      availability: [...formData.availability, { ...availabilityInput }]
    });

    // Reset time inputs but keep the selected day
    setAvailabilityInput({
      ...availabilityInput,
      startTime: '',
      endTime: ''
    });
  };

  const removeAvailability = (day) => {
    setFormData({
      ...formData,
      availability: formData.availability.filter(slot => slot.day !== day)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAddDoctor(formData);
    setFormData({
      name: '',
      specialty: '',
      qualification: '',
      experience: '',
      fees: '',
      image: '',
      availability: []
    });
  };

  useEffect(() => {
    // Fetch specialties when component mounts
    const fetchSpecialties = async () => {
      try {
        const response = await axios.get('/api/specialties');
        setSpecialties(response.data);
        if (response.data.length > 0) {
          setFormData(prev => ({ ...prev, specialty: response.data[0]._id }));
        }
      } catch (error) {
        console.error('Error fetching specialties:', error);
      }
    };
    
    fetchSpecialties();
  }, []);

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Add New Doctor</h2>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Specialty</label>
          <select
            name="specialty"
            value={formData.specialty}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
            required
          >
            <option value="">Select a specialty</option>
            {specialties.map(specialty => (
              <option key={specialty._id} value={specialty._id}>
                {specialty.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Qualification</label>
          <input
            type="text"
            name="qualification"
            value={formData.qualification}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Experience (years)</label>
          <input
            type="number"
            name="experience"
            value={formData.experience}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Consultation Fees ($)</label>
          <input
            type="number"
            name="fees"
            value={formData.fees}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Profile Image</label>
          <input
            type="file"
            name="image"
            onChange={handleImageChange}
            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100"
          />
        </div>

        {/* Availability Section */}
        <div className="md:col-span-2 mt-4">
          <h3 className="text-md font-medium text-gray-900 mb-3">Doctor Availability</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-gray-200 rounded-md bg-gray-50">
            <div>
              <label className="block text-sm font-medium text-gray-700">Day</label>
              <select
                name="day"
                value={availabilityInput.day}
                onChange={handleAvailabilityChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              >
                {daysOfWeek.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Time</label>
              <input
                type="time"
                name="startTime"
                value={availabilityInput.startTime}
                onChange={handleAvailabilityChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">End Time</label>
              <input
                type="time"
                name="endTime"
                value={availabilityInput.endTime}
                onChange={handleAvailabilityChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={addAvailability}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Slot
              </button>
            </div>
          </div>
          
          {/* Display added availability slots */}
          {formData.availability.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Added Availability:</h4>
              <div className="space-y-2">
                {formData.availability.map((slot, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 border border-gray-200 rounded-md">
                    <span className="text-sm">
                      {slot.day}: {slot.startTime} - {slot.endTime}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAvailability(slot.day)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-2 flex justify-end gap-3 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Add Doctor
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddDoctorForm;
